'use client';

import React, { useState } from 'react';


// ─── PEN HADITH ONBOARDING PANEL ─────────────────────────────────────────────
// Shown before first module is planted — the garden's origin story

export function PenHadithOnboardingPanel({
  visible,
  onEnter,
}: {
  visible: boolean;
  onEnter: () => void;
}) {
  if (!visible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(6,13,18,0.97)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        fontFamily: "'Courier New', monospace",
      }}
    >
      <div style={{ maxWidth: 560, width: '100%' }}>
        {/* Pen glyph */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'rgba(201,168,76,0.06)',
            border: '1px solid rgba(201,168,76,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
            fontSize: 22, color: '#c9a84c',
          }}>✦</div>
          <p style={{ fontSize: 8, color: 'rgba(201,168,76,0.5)', letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 8 }}>
            Before you enter the garden
          </p>
          <h2 style={{ fontFamily: "'Courier New', monospace", fontSize: 20, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '.05em', color: '#F5F0E8', marginBottom: 4 }}>
            The Garden's Origin Story
          </h2>
          <p style={{ fontFamily: "'Amiri', serif", fontSize: 20, color: 'rgba(201,168,76,0.5)', direction: 'rtl' }}>الْقَلَم</p>
        </div>

        {/* Pen Hadith */}
        <div style={{
          background: 'rgba(201,168,76,0.04)',
          border: '1px solid rgba(201,168,76,0.15)',
          borderLeft: '3px solid rgba(201,168,76,0.4)',
          borderRadius: '0 8px 8px 0',
          padding: '20px 20px 16px',
          marginBottom: 20,
        }}>
          <p style={{ fontSize: 8, color: 'rgba(201,168,76,0.5)', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 10 }}>
            Ubadah ibn al Samit · Sunan Abi Dawud 4700 · Grade Sahih
          </p>
          <p style={{
            fontFamily: "'Georgia', serif",
            fontStyle: 'italic',
            fontSize: 13,
            color: '#d4c5a9',
            lineHeight: 1.85,
          }}>
            "The first thing Allah created was the pen. He said to it: 'Write.' It asked: 'What should I write, my Lord?' He said: 'Write what was decreed about everything till the Last Hour comes.'"
          </p>
        </div>

        {/* Explanation */}
        <div style={{ marginBottom: 20, space: 12 }}>
          <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12, lineHeight: 1.8, marginBottom: 12 }}>
            This is not just a hadith — it is the garden's creation narrative. Before the garden existed, the decree was written. Before the modules were designed, the taqdir was set.
          </p>
          <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, lineHeight: 1.8, marginBottom: 12 }}>
            <strong style={{ color: '#d4c5a9' }}>The garden is a reading tool for a text that was written before time began.</strong> The caretaker doesn't generate data. They read what was already written.
          </p>
          <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 12, lineHeight: 1.8 }}>
            Everything you are about to encounter in this garden was already written. Your work is not to write — but to read.
          </p>
        </div>

        {/* Ibn Abbas hadith */}
        <div style={{
          background: 'rgba(138,138,184,0.04)',
          border: '1px solid rgba(138,138,184,0.15)',
          borderRadius: 6,
          padding: '14px 16px',
          marginBottom: 24,
        }}>
          <p style={{ fontSize: 8, color: 'rgba(138,138,184,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>
            Hadith 19 · 40 Hadith an-Nawawi
          </p>
          <p style={{
            fontFamily: "'Georgia', serif",
            fontStyle: 'italic',
            fontSize: 12,
            color: 'rgba(212,197,169,0.65)',
            lineHeight: 1.8,
          }}>
            "The pens have been lifted and the pages have dried."
          </p>
          <p style={{ color: 'rgba(212,197,169,0.35)', fontSize: 10, marginTop: 8, lineHeight: 1.6 }}>
            What was decreed has been decreed. What you will recognize in this garden was placed here for you to find.
          </p>
        </div>

        {/* Enter button */}
        <button
          onClick={onEnter}
          style={{
            width: '100%',
            background: 'rgba(201,168,76,0.08)',
            border: '1px solid rgba(201,168,76,0.3)',
            borderRadius: 6,
            padding: '14px 20px',
            cursor: 'pointer',
            fontFamily: "'Courier New', monospace",
            fontSize: 10,
            letterSpacing: '.15em',
            textTransform: 'uppercase',
            color: '#c9a84c',
            transition: 'all 0.2s',
          }}
        >
          Enter the Garden — Begin Reading ✦
        </button>
      </div>
    </div>
  );
}

// ─── FAJR TEMPORAL URGENCY NOTE ──────────────────────────────────────────────
// Added to the Fajr greeting — "you are entrusted with something that is running out"

export function FajrTemporalUrgencyNote() {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  return (
    <div style={{
      background: 'rgba(6,13,18,0.95)',
      border: '1px solid rgba(201,168,76,0.15)',
      borderLeft: '3px solid rgba(201,168,76,0.35)',
      borderRadius: '0 6px 6px 0',
      padding: '14px 16px',
      fontFamily: "'Courier New', monospace",
      marginBottom: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 7, color: 'rgba(201,168,76,0.5)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 6 }}>
            ◈ Fajr Reminder — Temporal Urgency
          </p>
          <p style={{
            fontFamily: "'Georgia', serif",
            fontStyle: 'italic',
            fontSize: 12,
            color: '#d4c5a9',
            lineHeight: 1.8,
            marginBottom: 8,
          }}>
            "Fisabilillah, in the way of Allah, you are entrusted with something that is running out, and you're entrusted with the means to do something in that period."
          </p>
          <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 11, lineHeight: 1.7 }}>
            What would it look like to be mindful of Allah in today's efforts?
          </p>
        </div>
        <button
          onClick={() => setDismissed(true)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(212,197,169,0.25)', fontSize: 12, flexShrink: 0, padding: '2px 4px' }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}

// ─── AL-QADIR NAMES ANNOTATION FIX ───────────────────────────────────────────
// Corrected to list all 9 Names present in the prose (not just 3)

export function AlQadirNamesAnnotation() {
  const [expanded, setExpanded] = useState(false);

  const namesInProse = [
    { name: 'Al-Qadir', arabic: 'الْقَادِر', meaning: 'The All-Powerful', occurrences: 3, note: 'The section\'s primary Name — appears in the title, the Pen paragraph, and the Taqdir paragraph' },
    { name: 'Al-Badi', arabic: 'الْبَدِيع', meaning: 'The Originator', occurrences: 1, note: '"Allah is Al-Badi; The Originator, of all information"' },
    { name: 'Al-Alim', arabic: 'الْعَلِيم', meaning: 'The All-Knowing', occurrences: 3, note: 'Appears in the Pen paragraph, Quran 2:32, and the Taqdir paragraph' },
    { name: 'Al-Khabir', arabic: 'الْخَبِير', meaning: 'All-Aware', occurrences: 1, note: '"He is Al-Alim; The All-Knowing, Al-Khabeer; All-Aware"' },
    { name: 'Al-Khaliq', arabic: 'الْخَالِق', meaning: 'The Creator', occurrences: 1, note: '"measurements given to creation by Al-Khaliq; The Creator, Al-Qadir; The All-Powerful"' },
    { name: 'Al-Basir', arabic: 'الْبَصِير', meaning: 'The All-Seeing', occurrences: 1, note: '"Allah is Al-Baseer; The All-Seeing, He provided us with vision"' },
    { name: 'An-Nur', arabic: 'النُّور', meaning: 'The Light', occurrences: 1, note: '"Al-Nur; The Light, granted us illumination for vision"' },
    { name: 'Al-Hakim', arabic: 'الْحَكِيم', meaning: 'The All-Wise', occurrences: 1, note: 'In Quran 2:32: "Indeed You, You Al-Alim (All-Knowing), Al-Hakim (The All-Wise)"' },
    { name: 'Al-Ali', arabic: 'الْعَلِيّ', meaning: 'The Most High', occurrences: 3, note: 'In Quran 3:26 and the Taqdir paragraph' },
  ];

  const additionalInVerses = [
    'Al-Haqq', 'Al-Latif', 'Al-Ghaniyy', 'Al-Hamid', 'Ar-Rauf', 'Ar-Rahim', 'Al-Sami', 'Al-Kabir',
  ];

  return (
    <div style={{
      background: 'rgba(6,13,18,0.95)',
      border: '1px solid rgba(201,168,76,0.15)',
      borderRadius: 6,
      padding: '14px 16px',
      fontFamily: "'Courier New', monospace",
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <p style={{ fontSize: 8, color: 'rgba(201,168,76,0.6)', letterSpacing: '.12em', textTransform: 'uppercase' }}>
          Names in This Section
        </p>
        <button
          onClick={() => setExpanded(v => !v)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 8, color: 'rgba(201,168,76,0.4)', fontFamily: 'inherit', letterSpacing: '.08em', textTransform: 'uppercase' }}
        >
          {expanded ? '▲ Collapse' : '▼ All Names'}
        </button>
      </div>

      {/* Primary names — always visible */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {namesInProse.map(n => (
          <div key={n.name} style={{
            padding: '3px 8px',
            background: 'rgba(201,168,76,0.06)',
            border: '1px solid rgba(201,168,76,0.18)',
            borderRadius: 3,
            display: 'flex', alignItems: 'center', gap: 5,
          }}>
            <span style={{ fontSize: 9, color: '#c9a84c', fontWeight: 600 }}>{n.name}</span>
            <span style={{ fontFamily: "'Amiri', serif", fontSize: 11, color: 'rgba(201,168,76,0.5)', direction: 'rtl' }}>{n.arabic}</span>
          </div>
        ))}
      </div>

      <p style={{ fontSize: 9, color: 'rgba(212,197,169,0.35)', marginBottom: expanded ? 12 : 0 }}>
        9 Names in prose · {additionalInVerses.length} additional Names in Quranic verses
      </p>

      {expanded && (
        <div style={{ marginTop: 12 }}>
          {/* Detailed name list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
            {namesInProse.map(n => (
              <div key={n.name} style={{
                padding: '8px 10px',
                background: 'rgba(201,168,76,0.04)',
                border: '1px solid rgba(201,168,76,0.1)',
                borderRadius: 4,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                  <span style={{ fontSize: 10, color: '#c9a84c', fontWeight: 700 }}>{n.name}</span>
                  <span style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: 'rgba(201,168,76,0.5)', direction: 'rtl' }}>{n.arabic}</span>
                  <span style={{ fontSize: 9, color: 'rgba(212,197,169,0.4)' }}>{n.meaning}</span>
                  <span style={{ fontSize: 8, color: 'rgba(212,197,169,0.25)', marginLeft: 'auto' }}>{n.occurrences}×</span>
                </div>
                <p style={{ fontSize: 9, color: 'rgba(212,197,169,0.4)', lineHeight: 1.5, fontStyle: 'italic' }}>{n.note}</p>
              </div>
            ))}
          </div>

          {/* Additional Names in verses */}
          <div style={{ padding: '10px 12px', background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.12)', borderRadius: 4 }}>
            <p style={{ fontSize: 8, color: 'rgba(45,122,71,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>
              Additional Names in Quranic Verses
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {additionalInVerses.map(n => (
                <span key={n} style={{ fontSize: 8, padding: '2px 6px', background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.15)', color: 'rgba(45,122,71,0.7)', borderRadius: 3 }}>{n}</span>
              ))}
            </div>
            <p style={{ fontSize: 9, color: 'rgba(212,197,169,0.3)', marginTop: 6, lineHeight: 1.5 }}>
              Total Names active in this section: approximately 17. The section's full Name ecology is richer than the prose Names alone.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PULL QUOTE FIX ───────────────────────────────────────────────────────────
// Replaces the non-verbatim pull quote with direct letter text

export function AlQadirPullQuoteCorrected() {
  return (
    <div style={{
      background: 'rgba(201,168,76,0.04)',
      border: '1px solid rgba(201,168,76,0.18)',
      borderLeft: '4px solid rgba(201,168,76,0.45)',
      borderRadius: '0 8px 8px 0',
      padding: '20px 20px 16px',
      fontFamily: "'Courier New', monospace",
    }}>
      <p style={{ fontSize: 7, color: 'rgba(201,168,76,0.45)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 10 }}>
        ◈ Pull Quote — Direct from the Letter
      </p>
      <p style={{
        fontFamily: "'Georgia', serif",
        fontStyle: 'italic',
        fontSize: 15,
        color: '#d4c5a9',
        lineHeight: 1.85,
        marginBottom: 10,
      }}>
        "Every measurement given to a thing is prescribed by the owner of the highest embodiment of that concept."
      </p>
      <p style={{ fontSize: 9, color: 'rgba(212,197,169,0.35)', lineHeight: 1.6 }}>
        — Al-Qadir Section, Taqdir Exploration Paragraph. This is the Huduth Sifati principle: attributes are not inherent to the thing — they are taqdir, prescribed measurements given by the One who embodies the highest essence of each quality.
      </p>
      <div style={{ marginTop: 10, padding: '8px 10px', background: 'rgba(138,138,184,0.04)', border: '1px solid rgba(138,138,184,0.1)', borderRadius: 3 }}>
        <p style={{ fontSize: 8, color: 'rgba(138,138,184,0.5)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>Note on Previous Version</p>
        <p style={{ fontSize: 9, color: 'rgba(212,197,169,0.35)', lineHeight: 1.6 }}>
          The previous pull quote ("The sequence of how Allah decides to do a thing is the Sunnah of Allah...") was a paraphrase sourced from the Leaf Sequence (page 28 of the PDF), not from this section. It has been replaced with the direct letter text above.
        </p>
      </div>
    </div>
  );
}

// ─── LEAF SEQUENCE FORWARD REFERENCE ANNOTATION ───────────────────────────────
// Corrected annotation noting the Leaf Sequence appears later in the letter

export function LeafSequenceForwardReference() {
  return (
    <div style={{
      background: 'rgba(45,122,71,0.04)',
      border: '1px solid rgba(45,122,71,0.15)',
      borderRadius: 6,
      padding: '14px 16px',
      fontFamily: "'Courier New', monospace",
    }}>
      <p style={{ fontSize: 7, color: 'rgba(45,122,71,0.5)', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 8 }}>
        ◈ Forward Reference — Leaf Sequence
      </p>
      <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.55)', lineHeight: 1.7, marginBottom: 8 }}>
        This section establishes the Qadr/Taqdir principle — every measurement in creation is decreed. The Leaf Sequence, which demonstrates this principle through a single leaf, appears later in the letter (pages 27–29).
      </p>
      <p style={{ fontSize: 10, color: 'rgba(212,197,169,0.4)', lineHeight: 1.7, marginBottom: 8 }}>
        The cross-link to Al-Qadir → Al-Khaliq → Al-Musawwir → Ar-Razzaq as a sequence of recognition is valid as a forward reference — but the Leaf Sequence does not appear in this section. It is a demonstration of the Taqdir principle that this section establishes.
      </p>
      <div style={{ padding: '8px 10px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 3 }}>
        <p style={{ fontSize: 9, color: 'rgba(201,168,76,0.5)', marginBottom: 4, letterSpacing: '.06em', textTransform: 'uppercase', fontSize: 7 }}>What this section actually grounds</p>
        <p style={{ fontSize: 9, color: 'rgba(212,197,169,0.45)', lineHeight: 1.6 }}>
          → IRL Lens: The Pen example is the foundational methodology — everything was written; the Lens reads what was decreed<br />
          → Keys to Kulli Haal: Sunnat Allah — the Order of Operations<br />
          → Names Reference: Al-Qadir, Al-Badi, Al-Alim, Al-Khabir, Al-Khaliq, Al-Basir, An-Nur, Al-Hakim, Al-Ali<br />
          → Ayat in Creation: Every measurement a sign of the One Who measured it
        </p>
      </div>
    </div>
  );
}

// ─── SAND AND GLASS IRL LENS NIYYAH ──────────────────────────────────────────
// Shown when the IRL Lens module is first planted — the module's niyyah statement

export function SandAndGlassNiyyahStatement({ context = 'module' }: { context?: 'module' | 'page' }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{
      background: 'rgba(6,13,18,0.95)',
      border: '1px solid rgba(45,138,135,0.2)',
      borderLeft: '3px solid rgba(45,138,135,0.45)',
      borderRadius: '0 8px 8px 0',
      padding: '18px 18px 14px',
      fontFamily: "'Courier New', monospace",
    }}>
      <p style={{ fontSize: 7, color: 'rgba(45,138,135,0.6)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 10 }}>
        {context === 'module' ? '◈ IRL Lens — Niyyah Statement' : '◈ The Lens as Entrusted Tool'}
      </p>

      {/* Core passage */}
      <p style={{
        fontFamily: "'Georgia', serif",
        fontStyle: 'italic',
        fontSize: 13,
        color: '#d4c5a9',
        lineHeight: 1.85,
        marginBottom: 12,
      }}>
        "Allah is Al-Baseer; The All-Seeing, He provided us with vision, and entrusted to us sand and glass; the means to develop lenses, a microscope, or binoculars — tools. Al-Nur; The Light, granted us illumination for vision accentuating a camera sensor to further expose images."
      </p>

      <p style={{ fontSize: 10, color: 'rgba(212,197,169,0.5)', lineHeight: 1.7, marginBottom: 10 }}>
        — Al-Qadir Section, Taqdir Exploration Paragraph
      </p>

      {/* Niyyah declaration */}
      <div style={{
        padding: '12px 14px',
        background: 'rgba(45,138,135,0.05)',
        border: '1px solid rgba(45,138,135,0.15)',
        borderRadius: 4,
        marginBottom: 10,
      }}>
        <p style={{ fontSize: 8, color: 'rgba(45,138,135,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>Niyyah of this Lens</p>
        <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.65)', lineHeight: 1.7 }}>
          You are planting a tool that was entrusted to you through sand and glass, through Names and Attributes, through the capacity for sight that was given before you chose to use it. The Lens is not your invention. It is your inheritance.
        </p>
      </div>

      {/* Expandable depth */}
      <button
        onClick={() => setExpanded(v => !v)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 8, color: 'rgba(45,138,135,0.45)', fontFamily: 'inherit', letterSpacing: '.08em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 5 }}
      >
        <span style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', display: 'inline-block' }}>▶</span>
        {expanded ? 'Collapse' : 'What does this mean?'}
      </button>

      {expanded && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            {
              label: 'Al-Baseer gave you sight',
              text: 'The faculty of seeing is a gift from Al-Baseer — The All-Seeing. You did not create your capacity to see. It was given. The IRL Lens extends this given capacity.',
              color: '#2d8a87',
            },
            {
              label: 'Sand and glass are entrusted Mata',
              text: 'The materials for building tools of seeing are a trust (Amanah), not a possession. Sand → glass → lens → microscope → binoculars. Each step is a stewardship of what was entrusted.',
              color: '#c9a84c',
            },
            {
              label: 'An-Nur provides the light',
              text: 'Al-Nur — The Light — granted illumination for vision. Without light, no lens functions. The IRL Lens operates by the same provision: An-Nur illuminates what the lens reveals.',
              color: '#D4A82A',
            },
            {
              label: 'The Lens is itself an Ayah',
              text: 'The IRL Lens is composed of Names — Al-Baseer (sight), An-Nur (light), Al-Khaliq (creation), Al-Musawwir (form). The tool recognizes that it too is composed of what it reads. The lens is Mata — its purpose is not the lens itself, but what the lens reveals.',
              color: '#2d7a47',
            },
          ].map(item => (
            <div key={item.label} style={{
              padding: '10px 12px',
              background: `${item.color}06`,
              border: `1px solid ${item.color}18`,
              borderRadius: 4,
            }}>
              <p style={{ fontSize: 8, color: item.color, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>{item.label}</p>
              <p style={{ fontSize: 10, color: 'rgba(212,197,169,0.5)', lineHeight: 1.6 }}>{item.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── TAQDIR WARD DATA FRAMING BANNER ─────────────────────────────────────────
// Shown above MRG ward data sections — frames all metrics as taqdir

export function TaqdirWardDataBanner() {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  return (
    <div style={{
      background: 'rgba(6,13,18,0.95)',
      border: '1px solid rgba(201,168,76,0.15)',
      borderRadius: 6,
      padding: '14px 16px',
      fontFamily: "'Courier New', monospace",
      marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 7, color: 'rgba(201,168,76,0.5)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 6 }}>
            ◈ Reading Ward Data as Taqdir
          </p>
          <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.6)', lineHeight: 1.7, marginBottom: 6 }}>
            Every metric below is a <strong style={{ color: '#c9a84c' }}>taqdir</strong> — an individual measure decreed to this ward. The number is not neutral. It is evidence of a state that the <strong style={{ color: '#d4c5a9' }}>Mizan evaluates</strong> and the <strong style={{ color: '#d4c5a9' }}>Amanah calls the caretaker to address</strong>.
          </p>
          <p style={{ fontSize: 10, color: 'rgba(212,197,169,0.35)', lineHeight: 1.6, fontStyle: 'italic' }}>
            "Every measurement given to a thing is prescribed by the owner of the highest embodiment of that concept." — Al-Qadir Section
          </p>
        </div>
        <button
          onClick={() => setDismissed(true)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(212,197,169,0.25)', fontSize: 12, flexShrink: 0 }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}

// ─── PLOT 5 AUDIT ENHANCEMENTS PANEL ─────────────────────────────────────────
// Consolidated panel for the open-letter page

export default function Plot5AuditEnhancements() {
  // Garden origin story panel removed — was auto-loading on page visit.
  // PenHadithOnboardingPanel is still exported for optional use elsewhere.

  return (
    <>
      {/* Plot 5 Audit Enhancements section */}
      <section
        id="section-plot5-audit"
        style={{
          background: 'rgba(6,13,18,0.98)',
          borderTop: '1px solid rgba(201,168,76,0.08)',
          padding: '80px 0',
        }}
      >
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <div style={{ marginBottom: 40 }}>
            <p className="font-mono text-[9px] tracking-widest uppercase mb-4" style={{ color: 'rgba(139,105,20,0.6)' }}>
              ✦ Plot 5 — Audit Enhancements · Al-Qadir Section Corrections
            </p>
            <h2 className="font-mono text-2xl font-black uppercase tracking-tight mb-4" style={{ color: '#F5F0E8' }}>
              The Qadr Principle as the Garden's Physics Engine
            </h2>
            <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 13, lineHeight: 1.7, maxWidth: 640, marginBottom: 8 }}>
              "This Decree includes the 'Taqdir', the individual measures of all things. We experience these measures in time and space." Every data point in the MRG is a taqdir — a measure given to that ward by the conditions that Allah's decree has allowed to unfold through human choices.
            </p>
          </div>

          {/* Taqdir ward data framing */}
          <div style={{ marginBottom: 32 }}>
            <p className="font-mono text-[8px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.35)' }}>
              Ward Data as Taqdir — Framing Banner
            </p>
            <TaqdirWardDataBanner />
          </div>

          {/* Corrected pull quote */}
          <div style={{ marginBottom: 32 }}>
            <p className="font-mono text-[8px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.35)' }}>
              Pull Quote — Corrected to Direct Letter Text
            </p>
            <AlQadirPullQuoteCorrected />
          </div>

          {/* Names annotation fix */}
          <div style={{ marginBottom: 32 }}>
            <p className="font-mono text-[8px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.35)' }}>
              Names Annotation — All 9 Names in Prose (Previously Listed 3)
            </p>
            <AlQadirNamesAnnotation />
          </div>

          {/* Leaf Sequence forward reference */}
          <div style={{ marginBottom: 32 }}>
            <p className="font-mono text-[8px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.35)' }}>
              Leaf Sequence Forward Reference — Corrected Annotation
            </p>
            <LeafSequenceForwardReference />
          </div>

          {/* Sand and glass IRL Lens niyyah */}
          <div style={{ marginBottom: 32 }}>
            <p className="font-mono text-[8px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.35)' }}>
              IRL Lens Niyyah Statement — Sand and Glass as Theological Origin
            </p>
            <SandAndGlassNiyyahStatement context="page" />
          </div>

          {/* Fajr temporal urgency */}
          <div style={{ marginBottom: 32 }}>
            <p className="font-mono text-[8px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.35)' }}>
              Fajr Greeting — Temporal Urgency Note
            </p>
            <FajrTemporalUrgencyNote />
          </div>

          {/* Pen Hadith preview button */}
          <div>
            <p className="font-mono text-[8px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.35)' }}>
              Pen Hadith Onboarding — Garden Origin Story Panel
            </p>
            <div style={{
              background: 'rgba(201,168,76,0.04)',
              border: '1px solid rgba(201,168,76,0.15)',
              borderRadius: 6,
              padding: '16px',
            }}>
              <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.7, marginBottom: 12 }}>
                The Pen Hadith onboarding panel is shown to first-time visitors before their first module is planted. It presents the garden's origin story: "Before the garden existed, the decree was written. Before the modules were designed, the taqdir was set. The garden is a reading tool for a text that was written before time began."
              </p>
              <button
                onClick={() => {
                  if (typeof window !== 'undefined') localStorage.removeItem('haal_pen_hadith_seen');
                }}
                style={{
                  background: 'rgba(201,168,76,0.08)',
                  border: '1px solid rgba(201,168,76,0.25)',
                  borderRadius: 4,
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontFamily: "'Courier New', monospace",
                  fontSize: 9,
                  letterSpacing: '.1em',
                  textTransform: 'uppercase',
                  color: '#c9a84c',
                }}
              >
                Preview Onboarding Panel ✦
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
